import React, { useRef, useState } from 'react';
import { Camera, Loader2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { UserAvatar } from './UserAvatar';
import { cn } from '@/lib/utils';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export interface AvatarUploadProps {
  avatarUrl?: string | null;
  firstName: string;
  lastName: string;
  onUpload: (file: File) => Promise<string>;
  onSave: (avatarUrl: string) => Promise<void>;
  /** Optional: called when user removes avatar. Must update profile with avatarUrl: null */
  onRemove?: () => Promise<void>;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  middleContent?: React.ReactNode;
}

export function AvatarUpload({
  avatarUrl,
  firstName,
  lastName,
  onUpload,
  onSave,
  onRemove,
  size = 'lg',
  className,
  middleContent,
}: AvatarUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = () => {
    if (uploading) return;
    setError(null);
    inputRef.current?.click();
  };

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Please select a JPEG, PNG, WebP, or GIF image.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'Image must be smaller than 5MB.';
    }
    return null;
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setError(null);
    e.target.value = '';

    try {
      const url = await onUpload(file);
      await onSave(url);
      toast.success('Avatar updated successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload avatar';
      setError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onRemove || !avatarUrl) return;
    setRemoving(true);
    setError(null);
    try {
      await onRemove();
      toast.success('Avatar removed');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove avatar';
      setError(message);
      toast.error(message);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className={cn('flex flex-col items-center gap-2', className)}>
      <button
        type="button"
        onClick={handleClick}
        disabled={uploading || removing}
        className="relative group focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-full"
        aria-label="Upload avatar"
      >
        <UserAvatar
          avatarUrl={avatarUrl ?? undefined}
          firstName={firstName}
          lastName={lastName}
          size={size}
        />
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity',
            (uploading || removing) && 'opacity-100'
          )}
          aria-hidden
        >
          {uploading || removing ? (
            <Loader2 className="h-8 w-8 text-white animate-spin" />
          ) : (
            <Camera className="h-8 w-8 text-white" />
          )}
        </div>
      </button>
      {middleContent}
      {avatarUrl && onRemove && (
        <button
          type="button"
          onClick={handleRemove}
          disabled={uploading || removing}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors disabled:opacity-50"
          aria-label="Remove avatar"
        >
          <Trash2 className="h-4 w-4" />
          Remove photo
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleChange}
        className="hidden"
        aria-hidden
      />
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400 text-center max-w-[200px]">
          {error}
        </p>
      )}
    </div>
  );
}
